import { useState } from 'react';
import BondingLevelDisplay from '../shared/BondingLevelDisplay';
import { THEME_COLORS } from '../../../constants/horseConstants';
import { resolveImageUrl } from '../../../utils/horseHelpers';

/**
 * Horse Info Card Component
 * Displays basic information about the horse including bonding level
 */
const HorseInfoCard = ({ mustang, onEdit, onDelete }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  // Get all photos (combine photo_url and photo_urls, remove duplicates)
  const getAllPhotos = () => {
    const photos = [];
    if (mustang.photo_url) photos.push(mustang.photo_url);
    if (mustang.photo_urls && Array.isArray(mustang.photo_urls)) {
      mustang.photo_urls.forEach(url => {
        if (url && !photos.includes(url)) photos.push(url);
      });
    }
    return photos;
  };
  
  const photos = getAllPhotos();
  const currentPhoto = photos[currentPhotoIndex] ? resolveImageUrl(photos[currentPhotoIndex]) : null;
  
  const nextPhoto = () => {
    if (photos.length > 1) {
      setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
    }
  };
  
  const prevPhoto = () => {
    if (photos.length > 1) {
      setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Horse Photo Gallery */}
      {currentPhoto ? (
        <div className="relative w-full aspect-square rounded-lg overflow-hidden">
          <img 
            src={currentPhoto} 
            alt={mustang.name}
            className="w-full h-full object-cover"
          />
          {/* Photo navigation arrows (only show if multiple photos) */}
          {photos.length > 1 && (
            <>
              <button
                onClick={prevPhoto}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition"
              >
                ‹
              </button>
              <button
                onClick={nextPhoto}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition"
              >
                ›
              </button>
              {/* Photo counter */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-black/50 rounded text-white text-xs">
                {currentPhotoIndex + 1} / {photos.length}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="w-full aspect-square rounded-lg bg-gray-200 flex items-center justify-center">
          <span className="text-4xl">🐴</span>
        </div>
      )}

      {/* Brand Symbols Display */}
      {mustang.brand_symbols && mustang.brand_symbols.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {/* First two symbols stacked - close together */}
          {mustang.brand_symbols.length >= 2 && (
            <div className="flex flex-col items-center gap-0.5">
              <div className="h-[9px] w-4">
                <img 
                  src={mustang.brand_symbols[0]} 
                  alt="Brand symbol 1"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="h-[11px] w-[18px]">
                <img 
                  src={mustang.brand_symbols[1]} 
                  alt="Brand symbol 2"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          )}
          {/* Single first symbol if only one */}
          {mustang.brand_symbols.length === 1 && (
            <div className="w-8 h-8">
              <img 
                src={mustang.brand_symbols[0]} 
                alt="Brand symbol 1"
                className="w-full h-full object-contain"
              />
            </div>
          )}
          {/* Last 6 symbols - smaller, closer together, with underline */}
          {mustang.brand_symbols.length > 2 && (
            <div className="flex flex-col">
              <div className="flex items-center" style={{ gap: '2px' }}>
                {mustang.brand_symbols.slice(2, 8).map((symbol, idx) => (
                  <div key={idx + 2} className="w-[22px] h-[22px]">
                    <img 
                      src={symbol} 
                      alt={`Brand symbol ${idx + 3}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ))}
              </div>
              {/* Underline spanning the 6 symbols */}
              <div 
                className="h-[2px] mt-1" 
                style={{ 
                  backgroundColor: '#000000',
                  width: `${Math.min(mustang.brand_symbols.length - 2, 6) * 24}px`
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Bonding Level */}
      <BondingLevelDisplay 
        level={mustang.bonding_level || 0} 
        showDetails={true}
      />

      {/* Horse Details */}
      <div className="space-y-2 text-sm">
        {mustang.tag_number && (
          <div className="flex justify-between">
            <span className="font-medium" style={{ color: THEME_COLORS.primary }}>Tag Number:</span>
            <span>{mustang.tag_number}</span>
          </div>
        )}
        {mustang.age && (
          <div className="flex justify-between">
            <span className="font-medium" style={{ color: THEME_COLORS.primary }}>Age:</span>
            <span>{mustang.age}</span>
          </div>
        )}
        {mustang.gender && (
          <div className="flex justify-between">
            <span className="font-medium" style={{ color: THEME_COLORS.primary }}>Gender:</span>
            <span>{mustang.gender}</span>
          </div>
        )}
        {mustang.color && (
          <div className="flex justify-between">
            <span className="font-medium" style={{ color: THEME_COLORS.primary }}>Color:</span>
            <span>{mustang.color}</span>
          </div>
        )}
        {mustang.hma && (
          <div className="flex justify-between">
            <span className="font-medium" style={{ color: THEME_COLORS.primary }}>HMA:</span>
            <span>{mustang.hma}</span>
          </div>
        )}
        {mustang.height && (
          <div className="flex justify-between">
            <span className="font-medium" style={{ color: THEME_COLORS.primary }}>Height:</span>
            <span>{mustang.height}</span>
          </div>
        )}
        {mustang.disposition && (
          <div className="flex justify-between">
            <span className="font-medium" style={{ color: THEME_COLORS.primary }}>Disposition:</span>
            <span>{mustang.disposition}</span>
          </div>
        )}
      </div>

      {/* Description */}
      {mustang.description && (
        <div className="pt-3 border-t">
          <div className="text-sm font-medium mb-1" style={{ color: THEME_COLORS.primary }}>
            About:
          </div>
          <div className="text-sm text-gray-600">{mustang.description}</div>
        </div>
      )}

      {/* Traits */}
      {mustang.traits && mustang.traits.length > 0 && (
        <div className="pt-3 border-t">
          <div className="text-sm font-medium mb-2" style={{ color: THEME_COLORS.primary }}>
            Traits:
          </div>
          <div className="flex flex-wrap gap-1">
            {mustang.traits.map((trait, idx) => (
              <span 
                key={idx}
                className="px-2 py-1 rounded-full text-xs"
                style={{ backgroundColor: '#6FCFD3', color: 'white' }}
              >
                {trait}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Adoption Method Badge */}
      {mustang.adoption_method && (
        <div className="pt-3 border-t">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium" style={{ color: THEME_COLORS.primary }}>
              Adoption Method:
            </span>
            <span 
              className="px-2 py-1 rounded text-xs font-medium uppercase"
              style={{ backgroundColor: '#EEC474', color: '#372F2F' }}
            >
              {mustang.adoption_method}
            </span>
          </div>
        </div>
      )}

      {/* Text Notes */}
      {(mustang.grooming || mustang.groundwork || mustang.daily_feeding) && (
        <div className="space-y-3 pt-3 border-t">
          {mustang.grooming && (
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: THEME_COLORS.primary }}>
                Grooming Notes:
              </div>
              <div className="text-sm text-gray-600">{mustang.grooming}</div>
            </div>
          )}
          {mustang.groundwork && (
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: THEME_COLORS.primary }}>
                Groundwork Notes:
              </div>
              <div className="text-sm text-gray-600">{mustang.groundwork}</div>
            </div>
          )}
          {mustang.daily_feeding && (
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: THEME_COLORS.primary }}>
                Daily Feeding Notes:
              </div>
              <div className="text-sm text-gray-600">{mustang.daily_feeding}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HorseInfoCard;